import { useCallback, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { useNavigation } from "../../../contexts/NavigationContext";
import { clearLocalAuthState } from "../../../utils/clearLocalAuthState";
import { passwordChangeRequest } from "../../../msalconfig";
import {
  PASSWORD_CHANGE_TEXT,
  passwordChangeOutcome,
} from "../domain/passwordChange";

// FE-234: runs B2C's password-reset flow in MSAL's popup. The password is
// typed only on B2C's page; the popup hands MSAL an authorization code, never
// the password, and this hook sends nothing itself.
//
// On success it signs the user out, so they sign in with the new password.
// MSAL caches the popup's account apart from the sign-in one (accounts are
// keyed by policy), so the popup's result is never made the active account,
// and the sign-out names no account, which signs out every cached one.
// On cancel or failure the user stays signed in and sees a fixed message.
export default function useChangePassword() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { setTimeSeriesFileName, clearNavigation } = useNavigation();
  const [state, setState] = useState({ status: "idle", text: "" });
  const inFlight = useRef(false);

  // The header's sign-out steps, in its order (HeaderPage.jsx:94, logOut).
  // A shared sign-out is a later item.
  const signOut = useCallback(() => {
    setTimeSeriesFileName(null);
    clearNavigation();
    clearLocalAuthState();
    sessionStorage.clear();
    navigate("/");
    instance.logoutRedirect();
  }, [instance, navigate, setTimeSeriesFileName, clearNavigation]);

  const start = useCallback(async () => {
    if (inFlight.current) return;
    // Without the flow's authority MSAL would fall back to the sign-in flow.
    if (!passwordChangeRequest.authority) {
      setState({ status: "failed", text: PASSWORD_CHANGE_TEXT.failed });
      return;
    }

    inFlight.current = true;
    setState({ status: "pending", text: "" });
    try {
      // The result (the popup's account and tokens) is deliberately unused.
      await instance.loginPopup(passwordChangeRequest);
    } catch (error) {
      inFlight.current = false;
      const { outcome, text } = passwordChangeOutcome(error);
      setState({ status: outcome, text });
      return;
    }

    setState({ status: "changed", text: PASSWORD_CHANGE_TEXT.changed });
    signOut();
  }, [instance, signOut]);

  return {
    start,
    status: state.status,
    text: state.text,
    pending: state.status === "pending",
  };
}
